/**
 * @file color.ts
 * @description Служебная цветовая математика оформления сайта (phase19.16 Р1):
 * hex ↔ каналы, HSL, затемнение, прозрачность. Чистые функции без
 * зависимостей; разбор `#hex` и наложение берутся из cabinet-theme/contrast
 * (один разбор цвета на платформу, не копия).
 *
 * Кто пользуется: derive.ts (наведение, линии), contrast.ts (подбор
 * ближайшего читаемого цвета в том же оттенке).
 */

import { parseHexColor, type RgbaColor } from "../cabinet-theme/contrast.js";

/** Цвет в HSL: оттенок 0–360, насыщенность и светлота 0–1. */
export interface HslColor {
  h: number;
  s: number;
  l: number;
}

/** Разбор цвета роли; невалидный цвет — ошибка вызывающего (fail fast). */
export function requireHexColor(value: string): RgbaColor {
  const parsed = parseHexColor(value);
  if (!parsed) throw new Error(`site_theme_color_invalid: ${value}`);
  return parsed;
}

const channelHex = (value: number): string =>
  Math.round(Math.min(255, Math.max(0, value))).toString(16).padStart(2, "0");

/**
 * Каналы → `#rrggbb` (альфа 1) или `#rrggbbaa`. Нижний регистр — чтобы
 * сравнение снимков черновика и ревизии не ловило разницу регистра.
 */
export function toHexColor(color: RgbaColor): string {
  const rgb = `#${channelHex(color.r)}${channelHex(color.g)}${channelHex(color.b)}`;
  return color.a >= 1 ? rgb : `${rgb}${channelHex(color.a * 255)}`;
}

/** RGB (0–255) → HSL. Альфа не участвует. */
export function rgbToHsl(color: RgbaColor): HslColor {
  const r = color.r / 255;
  const g = color.g / 255;
  const b = color.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: h * 60, s, l };
}

/** HSL → RGB (0–255) с заданной альфой. */
export function hslToRgb(hsl: HslColor, alpha = 1): RgbaColor {
  const { h, s, l } = hsl;
  if (s === 0) return { r: l * 255, g: l * 255, b: l * 255, a: alpha };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue = (t0: number): number => {
    let t = t0;
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const hn = h / 360;
  return {
    r: hue(hn + 1 / 3) * 255,
    g: hue(hn) * 255,
    b: hue(hn - 1 / 3) * 255,
    a: alpha,
  };
}

/**
 * Смешение с другим цветом в доле `amount` (0–1). Альфа исходного цвета
 * сохраняется: затемнение полупрозрачной кнопки не должно делать её плотной.
 */
export function mixRgb(color: RgbaColor, other: RgbaColor, amount: number): RgbaColor {
  const k = Math.min(1, Math.max(0, amount));
  return {
    r: color.r + (other.r - color.r) * k,
    g: color.g + (other.g - color.g) * k,
    b: color.b + (other.b - color.b) * k,
    a: color.a,
  };
}

/** Цвет с умноженной прозрачностью: линии = текст × 15 % (derive.ts). */
export function withAlpha(color: RgbaColor, alpha: number): RgbaColor {
  return { ...color, a: Math.min(1, Math.max(0, color.a * alpha)) };
}

export const BLACK: RgbaColor = { r: 0, g: 0, b: 0, a: 1 };
export const WHITE: RgbaColor = { r: 255, g: 255, b: 255, a: 1 };
